import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import Cookies from "js-cookie";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Loader2, X, Hash, MessageSquare, ZoomIn, ZoomOut } from "lucide-react";
import { debounce } from "lodash";
import MessageList from "../../components/messagelist";
import ChannelList from "../../components/channelList";
import ThreadView from "../../components/ThreadView";
import FooterBar from "../../components/FooterBar";
import AppBarComponent from "../../components/appbar";
import { useMobile } from "../../hooks/use-mobile";
import { getUserPreference, updateUserPreference, VIEW_TYPES } from "../../services/userPreferences.service";

import { Button } from "@/components/ui/button";
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
    SheetDescription
} from "@/components/ui/sheet";
import {
    Dialog,
    DialogContent,
    DialogTitle
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

import {
    fetchChannels,
    setArchiveFilter,
    setChannelPanelOpen,
    setFullscreenImage
} from "../../redux/actions/channel.action";
import {
    selectChannels,
    selectArchiveFilter,
    selectChannelPanelOpen,
    selectFullscreenImage,
    selectIsChannelsLoading
} from "../../redux/selectors/channel.selectors";

const ChannelListSkeleton = () => (
    <div className="p-2 space-y-1">
        <div className="flex items-center gap-2 mb-3 px-1">
            <Skeleton className="h-4 w-4 rounded" />
            <Skeleton className="h-3 w-20" />
        </div>
        {[...Array(8)].map((_, i) => (
            <div key={i} className="flex items-center gap-2 py-1 px-1">
                <Skeleton className="h-3 w-3 rounded" />
                <Skeleton className="h-3 flex-1" style={{ width: `${60 + Math.random() * 30}%` }} />
            </div>
        ))}
    </div>
);

const EmptyThreadState = () => (
    <div className="h-full flex flex-col items-center justify-center text-center p-4">
        <div className="w-12 h-12 rounded-xl bg-muted/50 flex items-center justify-center mb-3">
            <MessageSquare className="h-6 w-6 text-muted-foreground/50" />
        </div>
        <h3 className="font-medium text-sm mb-0.5 text-muted-foreground">No thread selected</h3>
        <p className="text-xs text-muted-foreground/70 max-w-[180px]">
            Click on a message to view replies
        </p>
    </div>
);

// Custom resize handle component - positioned at panel edge
const ResizeHandle = ({ position = 'right', onMouseDown }) => (
    <div
        className={cn(
            "group absolute top-0 bottom-0 z-20 flex items-center justify-center cursor-col-resize",
            "w-[6px] -translate-x-1/2",
            "hover:bg-primary/10 active:bg-primary/20 transition-colors",
            position === 'right' ? "right-0 translate-x-1/2" : "left-0 -translate-x-1/2"
        )}
        onMouseDown={onMouseDown}
    >
        <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-primary/60 rounded-full w-1 h-12" />
    </div>
);

const ChannelComponent = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { channelId } = useParams();
    const messageId = new URLSearchParams(location.search).get('messageId');
    const mobileScreen = useMobile();
    const containerRef = useRef(null);

    const accessToken = Cookies.get('access_token');
    const userSettings = useSelector((state) => state.auth.user_settings);

    const channels = useSelector(selectChannels);
    const archiveFilter = useSelector(selectArchiveFilter);
    const channelPanelOpen = useSelector(selectChannelPanelOpen);
    const fullscreenImage = useSelector(selectFullscreenImage);
    const isLoading = useSelector(selectIsChannelsLoading);

    // Panel width state - defaults will be loaded from API
    const [channelListWidth, setChannelListWidth] = useState(220);
    const [threadViewWidth, setThreadViewWidth] = useState(380);
    const [preferencesLoaded, setPreferencesLoaded] = useState(false);
    const preferencesLoadedRef = useRef(false);
    const [isResizing, setIsResizing] = useState(null); // 'channel' | 'thread' | null

    // Zoom state for fullscreen image
    const [imageZoom, setImageZoom] = useState(1);

    // Custom resize handler
    const handleMouseDown = useCallback((panel) => (e) => {
        e.preventDefault();
        setIsResizing(panel);

        const startX = e.clientX;
        const startChannelWidth = channelListWidth;
        const startThreadWidth = threadViewWidth;

        const handleMouseMove = (moveEvent) => {
            if (!containerRef.current) return;
            const containerRect = containerRef.current.getBoundingClientRect();

            if (panel === 'channel') {
                const newWidth = Math.min(
                    Math.max(160, startChannelWidth + (moveEvent.clientX - startX)),
                    600
                );
                setChannelListWidth(newWidth);
            } else if (panel === 'thread') {
                const newWidth = Math.min(
                    Math.max(280, startThreadWidth - (moveEvent.clientX - startX)),
                    Math.min(800, containerRect.width * 0.5)
                );
                setThreadViewWidth(newWidth);
            }
        };

        const handleMouseUp = () => {
            setIsResizing(null);
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';
    }, [channelListWidth, threadViewWidth]);

    // Load user preferences on mount
    useEffect(() => {
        if (!accessToken || preferencesLoadedRef.current) return;

        const loadPreferences = async () => {
            try {
                const preferences = await getUserPreference(VIEW_TYPES.CHANNEL_VIEW);
                if (preferences.channelListWidth) {
                    setChannelListWidth(preferences.channelListWidth);
                }
                if (preferences.threadViewWidth) {
                    setThreadViewWidth(preferences.threadViewWidth);
                }
                preferencesLoadedRef.current = true;
                setPreferencesLoaded(true);
            } catch (error) {
                console.error('Error loading user preferences:', error);
                preferencesLoadedRef.current = true;
                setPreferencesLoaded(true);
            }
        };

        loadPreferences();
    }, [accessToken]);

    // Debounced function to save preferences
    const savePreferences = useMemo(
        () => debounce(async (data) => {
            if (!accessToken || !preferencesLoadedRef.current) return;
            try {
                await updateUserPreference(VIEW_TYPES.CHANNEL_VIEW, data);
            } catch (error) {
                console.error('Error saving user preferences:', error);
            }
        }, 500),
        [accessToken]
    );

    useEffect(() => {
        if (!accessToken) {
            navigate('/');
        }
    }, [accessToken, navigate]);

    useEffect(() => {
        if (accessToken) {
            dispatch(fetchChannels());
        }
    }, [dispatch, accessToken]);

    useEffect(() => {
        if (channels.length > 0 && !channelId) {
            const defaultChannelId = userSettings?.last_viewed_channel || channels[0].id;
            navigate(`/channels/${defaultChannelId}`, { replace: true });
        }
    }, [channels, channelId, userSettings, navigate]);

    // Save widths to API when they change (debounced)
    useEffect(() => {
        if (!mobileScreen && preferencesLoadedRef.current) {
            savePreferences({
                channelListWidth,
                threadViewWidth,
            });
        }
    }, [channelListWidth, threadViewWidth, mobileScreen, savePreferences]);

    // Cleanup debounced function on unmount
    useEffect(() => {
        return () => {
            savePreferences.cancel();
        };
    }, [savePreferences]);

    const hasActiveThread = !!messageId;

    return (
        <div className="relative flex h-full min-h-0 min-w-0 w-full flex-col overflow-hidden box-border bg-background pb-12">
            {/* Loading overlay */}
            {isLoading && (
                <div className="absolute inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm">
                    <div className="flex flex-col items-center gap-2">
                        <Loader2 className="h-8 w-8 animate-spin text-primary" />
                        <span className="text-xs font-medium text-muted-foreground">Loading...</span>
                    </div>
                </div>
            )}

            {/* AppBar */}
            <AppBarComponent />

            {/* Main Layout Container - Compact 3-panel layout */}
            <div
                ref={containerRef}
                className={cn(
                    "flex min-h-0 min-w-0 flex-1 h-full w-full overflow-hidden",
                    isResizing && "select-none"
                )}
            >
                {/* Channel List - Left Panel */}
                {mobileScreen ? (
                    <Sheet
                        open={channelPanelOpen}
                        onOpenChange={(open) => dispatch(setChannelPanelOpen(open))}
                    >
                        <SheetContent
                            side="left"
                            className="w-[280px] max-w-[85vw] p-0 flex flex-col"
                        >
                            <SheetHeader className="px-3 py-2 border-b shrink-0">
                                <SheetTitle className="flex items-center gap-1.5 text-sm">
                                    <Hash className="h-4 w-4 text-primary" />
                                    Channels
                                </SheetTitle>
                                <SheetDescription className="text-[10px]">
                                    Select a channel
                                </SheetDescription>
                            </SheetHeader>
                            <div className="flex-1 overflow-hidden">
                                {channels.length === 0 && isLoading ? (
                                    <ChannelListSkeleton />
                                ) : (
                                    <ChannelList />
                                )}
                            </div>
                        </SheetContent>
                    </Sheet>
                ) : (
                    <div
                        className="relative h-full shrink-0 bg-card"
                        style={{ width: channelListWidth }}
                    >
                        <div className="h-full overflow-hidden">
                            {channels.length === 0 && isLoading ? (
                                <ChannelListSkeleton />
                            ) : (
                                <ChannelList />
                            )}
                        </div>
                        {/* Right resize handle */}
                        <ResizeHandle
                            position="right"
                            onMouseDown={handleMouseDown('channel')}
                        />
                        <div
                            className="absolute right-0 top-0 bottom-0 w-px bg-border"
                            style={{ right: 0 }}
                        />
                    </div>
                )}

                {/* Message Area - Center Panel (flexible) */}
                <div className="flex-1 h-full overflow-hidden flex flex-col min-w-[200px] bg-background">
                    {!channelId ? (
                        <div className="h-full flex flex-col items-center justify-center text-center p-4">
                            <div className="w-14 h-14 rounded-xl bg-muted/50 flex items-center justify-center mb-3">
                                <Hash className="h-7 w-7 text-muted-foreground/50" />
                            </div>
                            <h3 className="font-medium text-sm mb-0.5">Welcome</h3>
                            <p className="text-xs text-muted-foreground/70 max-w-[200px]">
                                Select a channel to view messages
                            </p>
                        </div>
                    ) : (
                        <MessageList />
                    )}
                </div>

                {/* Thread Area - Right Panel */}
                {mobileScreen ? (
                    <Sheet
                        open={hasActiveThread}
                        onOpenChange={(open) => !open && navigate(`/channels/${channelId}`)}
                    >
                        <SheetContent
                            side="right"
                            className="w-full sm:w-[90vw] sm:max-w-[400px] p-0 flex flex-col"
                            hideCloseButton
                        >
                            <ThreadView />
                        </SheetContent>
                    </Sheet>
                ) : (
                    <div
                        className="relative h-full shrink-0 bg-muted/5"
                        style={{ width: threadViewWidth }}
                    >
                        {/* Left resize handle */}
                        <ResizeHandle
                            position="left"
                            onMouseDown={handleMouseDown('thread')}
                        />
                        <div
                            className="absolute left-0 top-0 bottom-0 w-px bg-border"
                            style={{ left: 0 }}
                        />
                        <div className="h-full overflow-hidden">
                            {hasActiveThread ? (
                                <ThreadView />
                            ) : (
                                <EmptyThreadState />
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Fullscreen Image Modal */}
            <Dialog
                open={!!fullscreenImage}
                onOpenChange={(open) => {
                    if (!open) {
                        dispatch(setFullscreenImage(null));
                        setImageZoom(1); // Reset zoom on close
                    }
                }}
            >
                <DialogContent
                    className="max-w-[95vw]! max-h-[95vh]! p-0 bg-transparent border-none shadow-none overflow-visible"
                    showCloseButton={false}
                >
                    <DialogTitle className="sr-only">Image Preview</DialogTitle>
                    <div className="relative flex items-center justify-center w-full h-full">
                        {/* Top controls - Close button */}
                        <Button
                            variant="ghost"
                            size="icon"
                            className="absolute -top-10 right-0 z-10 bg-black/50 hover:bg-black/70 text-white rounded-full"
                            onClick={() => {
                                dispatch(setFullscreenImage(null));
                                setImageZoom(1);
                            }}
                        >
                            <X className="h-5 w-5" />
                            <span className="sr-only">Close</span>
                        </Button>

                        {/* Zoom controls - Right corner, aligned with close button */}
                        <div className="absolute top-2 right-0 z-10 flex flex-col gap-1">
                            <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 bg-black/50 hover:bg-black/70 text-white rounded-full disabled:opacity-40"
                                onClick={() => setImageZoom(prev => Math.min(prev + 0.25, 3))}
                                disabled={imageZoom >= 3}
                            >
                                <ZoomIn className="h-4 w-4" />
                                <span className="sr-only">Zoom In</span>
                            </Button>
                            <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 bg-black/50 hover:bg-black/70 text-white rounded-full disabled:opacity-40"
                                onClick={() => setImageZoom(prev => Math.max(prev - 0.25, 0.5))}
                                disabled={imageZoom <= 0.5}
                            >
                                <ZoomOut className="h-4 w-4" />
                                <span className="sr-only">Zoom Out</span>
                            </Button>
                        </div>

                        {fullscreenImage && (
                            <div className="overflow-auto max-w-[90vw] max-h-[85vh] rounded-lg">
                                <img
                                    src={fullscreenImage}
                                    alt="Fullscreen preview"
                                    className="object-contain rounded-lg transition-transform duration-200"
                                    style={{
                                        transform: `scale(${imageZoom})`,
                                        transformOrigin: 'center center',
                                        maxWidth: imageZoom > 1 ? 'none' : '90vw',
                                        maxHeight: imageZoom > 1 ? 'none' : '85vh'
                                    }}
                                />
                            </div>
                        )}
                    </div>
                </DialogContent>
            </Dialog>

            {/* Footer Bar */}
            {!mobileScreen && (
                <FooterBar
                    fixed={true}
                    showArchiveToggle={true}
                    archiveValue={archiveFilter}
                    onArchiveChange={(e, val) => val !== null && dispatch(setArchiveFilter(val))}
                />
            )}
        </div>
    );
};

export default ChannelComponent;
